// RITA-1498 (Nutzungserfassung Phase 1b) — Klick-Ziel und Klick-Position.
//
// Regel (Konzept 3.4, Entscheidung 7): Beschriftungen werden nur bei ausdrücklich markierten
// Elementen (data-usage="bereich.aktion") gespeichert. Für alle anderen bedienbaren Elemente
// gibt es nur die Elementart und einen technischen Schlüssel (data-testid, id oder das
// Routen-Muster eines Links). Ziffernfolgen in Kennungen werden durch '#' ersetzt, damit keine
// Datensatz-Nummern (z. B. "log-row-42") ins Ereignis gelangen. Text, Werte und Namen nie.
import { toRoutePattern, UNKNOWN_ROUTE } from './routePattern';

export const USAGE_ATTRIBUTE = 'data-usage';
export const MAX_KEY_LENGTH = 128;
const MAX_IDENT_LENGTH = 96;
const MAX_KIND_LENGTH = 16;

export interface ClickTarget {
  key: string;
  kind: string;
  labeled: boolean;
}

/**
 * Was als Bedienelement gilt. Exportiert, weil die Totklick-Erkennung (deadClick.ts, RITA-1520)
 * dieselbe Liste als Vorbedingung braucht: ein Totklick ist genau dann möglich, wenn über dem
 * Klickpunkt KEINES dieser Elemente liegt. Zwei Listen würden auseinanderlaufen.
 */
export const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'summary',
  'select',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="submit"]',
  'input[type="button"]',
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="option"]',
  '[role="link"]',
].join(', ');

const ROLE_KINDS: Record<string, string> = {
  button: 'button',
  tab: 'tab',
  menuitem: 'menuitem',
  switch: 'switch',
  checkbox: 'checkbox',
  radio: 'radio',
  option: 'option',
  link: 'link',
};

export const elementKind = (element: Element): string => {
  const role = (element.getAttribute('role') || '').toLowerCase();
  if (ROLE_KINDS[role]) return ROLE_KINDS[role];
  const tag = element.tagName.toUpperCase();
  if (tag === 'BUTTON') return 'button';
  if (tag === 'A') return element.hasAttribute('href') ? 'link' : 'other';
  if (tag === 'INPUT') {
    const type = (element.getAttribute('type') || 'text').toLowerCase();
    if (type === 'checkbox' || type === 'radio') return type;
    if (type === 'submit' || type === 'button') return 'button';
    return 'input';
  }
  if (tag === 'SELECT') return 'select';
  if (tag === 'SUMMARY') return 'summary';
  return 'other';
};

const sanitizeIdent = (raw: string): string =>
  raw
    .trim()
    .replace(/\d+/g, '#')
    .replace(/[^A-Za-z0-9_.:/#-]/g, '_')
    .slice(0, MAX_IDENT_LENGTH);

const linkIdent = (element: Element): string => {
  const href = (element.getAttribute('href') || '').trim();
  if (href.startsWith('/')) return toRoutePattern(href);
  if (/^https?:\/\//i.test(href)) return 'extern';
  if (href.startsWith('#')) return 'anker';
  if (/^(mailto|tel):/i.test(href)) return href.split(':')[0].toLowerCase();
  return UNKNOWN_ROUTE;
};

/** Bestimmt Schlüssel, Art und Markierung des geklickten Bedienelements; null = kein Bedienelement. */
export const resolveClickTarget = (start: Element | null): ClickTarget | null => {
  if (!start || typeof start.closest !== 'function') return null;

  const marked = start.closest(`[${USAGE_ATTRIBUTE}]`);
  if (marked) {
    const key = (marked.getAttribute(USAGE_ATTRIBUTE) || '').trim().slice(0, MAX_KEY_LENGTH);
    if (key.length > 0) {
      return { key, kind: elementKind(marked).slice(0, MAX_KIND_LENGTH), labeled: true };
    }
  }

  const interactive = start.closest(INTERACTIVE_SELECTOR);
  if (!interactive) return null;
  const kind = elementKind(interactive);
  if (kind === 'input' || kind === 'other') return null;

  const ident =
    kind === 'link'
      ? linkIdent(interactive)
      : sanitizeIdent(interactive.getAttribute('data-testid') || interactive.getAttribute('id') || '');
  const key = ident ? `${kind}:${ident}` : kind;
  return { key: key.slice(0, MAX_KEY_LENGTH), kind: kind.slice(0, MAX_KIND_LENGTH), labeled: false };
};

export interface ClickPoint {
  clientX: number;
  pageY: number;
}

export interface ClickWindow {
  innerWidth: number;
  innerHeight: number;
  scrollY: number;
}

// Entspricht clickProps in batchSchema.ts (strict): genau diese Felder.
// Typ-Alias statt Interface, damit der Wert als Record<string, UsagePropValue> durchgeht.
export type ClickProps = {
  x_rel: number;
  y_doc: number;
  scroll_y: number;
  viewport_w: number;
  viewport_h: number;
  element_kind: string;
  labeled: boolean;
};

const clampInt = (value: unknown, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(Number(value) || 0)));

export const buildClickProps = (
  point: ClickPoint,
  win: ClickWindow,
  target: Pick<ClickTarget, 'kind' | 'labeled'>,
): ClickProps => {
  const width = Math.max(1, Number(win.innerWidth) || 1);
  const xRel = Math.min(1, Math.max(0, (Number(point.clientX) || 0) / width));
  return {
    x_rel: Math.round(xRel * 10000) / 10000,
    y_doc: clampInt(point.pageY, 0, Number.MAX_SAFE_INTEGER),
    scroll_y: clampInt(win.scrollY, 0, Number.MAX_SAFE_INTEGER),
    viewport_w: clampInt(win.innerWidth, 1, 65535),
    viewport_h: clampInt(win.innerHeight, 1, 65535),
    element_kind: target.kind.slice(0, MAX_KIND_LENGTH),
    labeled: target.labeled,
  };
};
